/**
 * Backend REST/WebSocket 계약 타입 (계획서 §12).
 *
 * enum 값은 backend `app/domain/enums.py` 와 문자열이 정확히 일치해야 한다.
 */

export const EQUIPMENT_IDS = [
  "WELDER",
  "EPOXY",
  "DRYER",
  "DUST_COLLECTOR",
  "COMPRESSOR",
] as const;
export type EquipmentId = (typeof EQUIPMENT_IDS)[number];

/**
 * 허용 배속 (계획서 §7.2).
 *
 * backend `app/simulation/clock.py::ALLOWED_SPEEDS` 와 반드시 같아야 한다.
 * 다른 값을 보내면 `422 INVALID_SPEED` 다.
 */
export const SPEED_OPTIONS = [1, 120, 240] as const;
export type SpeedMultiplier = (typeof SPEED_OPTIONS)[number];

export const RUN_STATES = [
  "IDLE",
  "RUNNING",
  "INCIDENT_ACTIVE",
  "PLANNING",
  "WAIT_PLAN_APPROVAL",
  "ADVANCING_TO_START",
  "WAIT_HUMAN_START",
  "WAIT_PLC_ACK",
  "EXECUTING",
  "WAIT_RISK_CONFIRMATION",
  "PLAN_GENERATION_FAILED",
  "COMPLETE",
] as const;
export type RunState = (typeof RUN_STATES)[number];

export type StrategyCode = "A" | "B";

export type DryerMode =
  | "OFF"
  | "PREHEAT"
  | "STABILIZE"
  | "HOLD"
  | "DRYING"
  | "COOLDOWN"
  | "COMPLETE";

export type AgentStatus = "IDLE" | "RUNNING" | "WAITING" | "COMPLETE" | "ERROR";

export type ApprovalGrade = "AUTO_APPROVE" | "TIMED_AUTO_APPROVE" | "EXPLICIT_APPROVAL";

/** 시각 변경·배속·재생이 잠기는 상태 (§7.2). */
export const CLOCK_LOCKED_STATES: readonly RunState[] = ["WAIT_HUMAN_START", "WAIT_PLC_ACK"];

/** application/problem+json 오류 본문 (§12.6). */
export interface ProblemDetail {
  status: number;
  code: string;
  title: string;
  detail: string;
  trace_id: string;
  [key: string]: unknown;
}

export interface CurrentUser {
  user_id: string;
  username: string;
}

export interface HealthCheck {
  status: "ok" | "error" | "skipped";
  detail: string;
}

export interface HealthReadyResponse {
  status: "ready" | "degraded" | "unready";
  checks: Record<"seed" | "postgres" | "neo4j", HealthCheck>;
}

/** WebSocket envelope (§12.6). */
export interface WsEnvelope<TPayload = unknown> {
  event_id: string;
  sequence: number;
  type: WsEventType;
  run_id: string;
  occurred_at: string;
  payload: TPayload;
}

export const WS_EVENT_TYPES = [
  "clock.updated",
  "snapshot.updated",
  "agent.status",
  "workflow.node",
  "plan.ready",
  "approval.updated",
  "human_start.required",
  "plc.updated",
  "audit.created",
  "run.completed",
] as const;
export type WsEventType = (typeof WS_EVENT_TYPES)[number];

// --------------------------------------------------------------------------- //
// 운전 화면 (§13.1~§13.5)
// --------------------------------------------------------------------------- //

/** 시계 (§12.2). */
export interface ClockPayload {
  virtual_at: string;
  state: RunState;
  speed_multiplier: number;
  is_playing: boolean;
  /** `WAIT_HUMAN_START`·`WAIT_PLC_ACK` — 시계 조작 자체가 잠긴다. */
  is_locked: boolean;
  /** 재생했을 때 가상시계가 실제로 흐르는 상태인가 (§7.2). */
  can_advance: boolean;
}

export interface IncidentPayload {
  equipment_id: EquipmentId;
  started_at: string;
  repair_minutes: number;
  ends_at: string;
  extension_probability_pct: string;
}

/** A/B 후보 하나 (§13.4). 수치는 문자열이다 — Decimal 을 float 으로 바꾸지 않는다. */
export interface CandidatePayload {
  dryer_started_at: string;
  ready_at: string;
  first_batch_eta: string;
  cooldown_completed_at: string;
  hold_minutes: number;
  eta_margin_minutes: number;
  total_kwh: string;
  total_cost_won: string;
  time_change_pct: string;
  cost_change_pct: string;
  final_change_pct: string;
  approval_grade: ApprovalGrade;
  critical_change: boolean;
}

export interface PlanPayload {
  plan_version?: number;
  recommended?: StrategyCode;
  recommendation_reasons?: string[];
  baseline_cost_won?: string;
  candidates?: Record<StrategyCode, CandidatePayload>;
}

export type ProductionState = "RUNNING" | "STOPPED" | "BREAK_STOPPED" | "FAULT_STOPPED";

/** 용접기·에폭시 표시 필드 (§13.2). */
export interface ProductionEquipment {
  state: ProductionState;
  units_completed: number;
  cycle_seconds: string;
  daily_target_units: number;
  finished_at: string;
  wip: number;
  production_allowed: boolean;
  power_kw: string;
  repair_elapsed_seconds: number | null;
  repair_total_seconds: number | null;
}

/** 건조기 표시 필드 (§13.2). */
export interface DryerEquipment {
  mode: DryerMode;
  temperature_c: string;
  heater_duty_pct: string;
  fan_on: boolean;
  stabilize_elapsed_seconds: number;
  stabilize_total_seconds: number;
  ready_at: string;
  cooldown_completed_at: string;
  power_kw: string;
  concurrent_units: number;
}

/** 집진기 표시 필드 (§13.2). */
export interface DustCollectorEquipment {
  running: boolean;
  hz: string;
  power_kw: string;
  follows_dryer_mode: DryerMode;
}

/** 컴프레서 필드 (§13.2). `model_source`는 모델 출처 메타데이터로 유지한다. */
export interface CompressorEquipment {
  running: boolean;
  target_mpa: string;
  hz: string;
  tank_pct: string;
  power_kw: string;
  consumers: EquipmentId[];
  model_source: string;
}

export interface EquipmentSnapshot {
  welder: ProductionEquipment;
  epoxy: ProductionEquipment;
  dryer: DryerEquipment | null;
  dust_collector: DustCollectorEquipment | null;
  compressor: CompressorEquipment;
}

export interface PlcPayload {
  message_id: string;
  plan_version: number;
  acked: boolean;
  targets: Record<string, Record<string, string>>;
}

export interface AuditItem {
  code: string;
  message: string;
}

/** `GET /runs/{run_id}/state` 전체 snapshot (§12.6). */
export interface RunSnapshot {
  run_id: string;
  parent_run_id: string | null;
  clock: ClockPayload;
  incident: IncidentPayload | null;
  plan: PlanPayload;
  selected_strategy: StrategyCode | null;
  plan_approved: boolean;
  approval_timer_remaining_seconds: number | null;
  plc: PlcPayload | null;
  equipment: EquipmentSnapshot;
  human_wait_wall_seconds: number | null;
  audit: AuditItem[];
  rule_version: string;
  data_version: string;
  ws_sequence?: number;
}

/** Agent 상세 (§8.11, §13.3). */
export interface AgentToolCallView {
  tool_call_id: string;
  tool_name: string;
  status: string;
  elapsed_ms: number | null;
  fact_count: number;
  cache_hit: boolean;
  error_code: string | null;
}

export interface AgentAdviceView {
  agent_id: string;
  equipment_id: string | null;
  status_summary: string;
  fact_refs: string[];
  risk_explanation: string;
  proposal_explanation: string;
  display_message: string;
  data_complete: boolean;
  missing_fact_names: string[];
}

export interface AgentRunView {
  agent_run_id: string;
  agent_id: string;
  status: AgentStatus;
  provider: string | null;
  model: string | null;
  used_fallback: boolean;
  attempt: number;
  elapsed_ms: number | null;
  required_tool_completed: boolean;
  error_code: string | null;
  advice: AgentAdviceView | null;
  tool_calls: AgentToolCallView[];
}

export interface AgentsResponse {
  run_id: string;
  /** Agent 분석이 진행 중인가. 수 분 걸리므로 화면이 "없음" 대신 "분석 중" 을 띄운다. */
  analyzing: boolean;
  agents: AgentRunView[];
}

/** Ontology 부분 그래프 (§10, §13.5). */
export interface OntologyNode {
  id: string;
  labels: string[];
  caption: string;
  properties: Record<string, string>;
}

export interface OntologyRelationship {
  id: string;
  from: string;
  to: string;
  type: string;
  caption: string;
}

export interface OntologyResponse {
  run_id: string;
  available: boolean;
  nodes: OntologyNode[];
  relationships: OntologyRelationship[];
}

export interface HumanStartResponse {
  message_id: string;
  run_id: string;
  plan_version: number;
  targets: Record<string, Record<string, string>>;
  acked: boolean;
}

/** LangGraph 진행 상태 (§12.4, §13.5). */
export interface WorkflowNodeView {
  node: string;
  agent_id: string;
  status: AgentStatus;
  provider: string | null;
  model: string | null;
  elapsed_ms: number | null;
  tool_calls: string[];
  error_code: string | null;
}

export interface WorkflowInterrupt {
  kind: string | null;
  value: Record<string, unknown>;
}

export interface WorkflowResponse {
  run_id: string;
  available: boolean;
  next: string[];
  interrupts: WorkflowInterrupt[];
  nodes: WorkflowNodeView[];
  failed_agents?: string[];
  plan_decision?: Record<string, unknown>;
  human_start?: Record<string, unknown>;
  plc_ack?: Record<string, unknown>;
  activated?: boolean;
}
